import { useState } from "react";
import { Mail, MessageCircle, Send, Shield } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Reveal, MaskedLines } from "@/components/Reveal";

const inputCls =
  "w-full border-b border-white/15 bg-transparent py-3 text-sm text-white placeholder:text-white/30 focus:border-velvet-gold focus:outline-none";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      // Reuse the /enquiries endpoint — treats subject as companion_id placeholder.
      await api.post("/enquiries", {
        companion_id: "contact-us",
        name: form.name,
        email: form.email,
        phone: "",
        date: "",
        message: `[${form.subject || "General enquiry"}] ${form.message}`,
      });
      toast.success("Message sent. We'll reply within 24 hours.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen pt-28 md:pt-32" data-testid="contact-page">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          {/* Left — copy */}
          <div className="lg:col-span-5">
            <Reveal>
              <p className="mb-6 text-xs uppercase tracking-luxe text-velvet-gold">Talk to us</p>
              <MaskedLines
                lines={["Get in", <span key="t" className="italic text-velvet-rose">touch.</span>]}
                lineClassName="font-heading text-5xl font-light leading-[0.95] tracking-tight text-white sm:text-6xl md:text-7xl"
              />
              <p className="mt-8 text-base font-light leading-relaxed text-white/60">
                For membership questions, verification support, advertiser enquiries or press —
                the Vixera concierge team will answer discreetly within 24 hours.
              </p>
            </Reveal>

            <div className="mt-12 space-y-6">
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center border border-velvet-gold/40 text-velvet-gold">
                  <Mail size={16} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-luxe text-white/40">Email</p>
                  <a href="mailto:hello@vixera.co.za" data-testid="contact-email-link"
                    className="text-sm text-white hover:text-velvet-gold">
                    hello@vixera.co.za
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center border border-velvet-gold/40 text-velvet-gold">
                  <MessageCircle size={16} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-luxe text-white/40">WhatsApp Concierge</p>
                  <a
                    href="https://wa.me/27600000000"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-link"
                    className="text-sm text-white hover:text-velvet-gold"
                  >
                    +27 60 000 0000
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center border border-velvet-gold/40 text-velvet-gold">
                  <Shield size={16} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-luxe text-white/40">Safety & Trust</p>
                  <a href="mailto:safety@vixera.co.za" className="text-sm text-white hover:text-velvet-gold">
                    safety@vixera.co.za
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="space-y-6 border border-white/10 bg-velvet-coal/40 p-8 md:p-12"
            >
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <input
                  required
                  type="text"
                  placeholder="Full name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputCls}
                  data-testid="contact-name"
                />
                <input
                  required
                  type="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputCls}
                  data-testid="contact-email"
                />
              </div>
              <input
                type="text"
                placeholder="Subject"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className={inputCls}
                data-testid="contact-subject"
              />
              <textarea
                required
                rows={6}
                placeholder="How can we help?"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className={`${inputCls} resize-none`}
                data-testid="contact-message"
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit"
                className="inline-flex items-center gap-2 rounded-full bg-velvet-rose px-8 py-4 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-velvet-gold disabled:opacity-50"
              >
                <Send size={14} />
                {sending ? "Sending…" : "Send message"}
              </button>
              <p className="text-[10px] uppercase tracking-luxe text-white/30">
                Confidential. We never share your details.
              </p>
            </form>
          </div>
        </div>
        <div className="h-32" />
      </div>
    </div>
  );
}
